import React from 'react';
import PropTypes from 'prop-types';
import { contentTop, groupOf, heroTopAt, navHeight, navTopAt } from '../layout';
import AppFrame from './AppFrame';
import ProfileNav from './profile/ProfileNav';
import { AddressDialog, CompletionPopover, PasswordDialog, PhoneDialog, PhotoDialog, ProfileHero } from './profile/ProfileHero';
import { AgendaPane, CalendarPane } from './profile/CalendarPanes';
import { FilesPane, QuizDialog, SopViewerDialog, TrainingDialog } from './profile/FilesPanes';
import { ProjectsPane, VisitsDialog } from './profile/ProjectsPanes';
import { LeaveFormPane, LeaveListPane } from './profile/LeavePanes';
import { ExpenseEditDialog, ExpenseFormPane, ExpenseTrackingPane } from './profile/ExpensePanes';
import { AnnouncementsPane, BugReportPane, MessagesPane } from './profile/ContactPanes';
import { CvPane } from './profile/CvPane';

const PANES = {
  takvim: CalendarPane,
  ajandam: AgendaPane,
  dosyalar: FilesPane,
  projelerim: ProjectsPane,
  'izin-talep': LeaveFormPane,
  'izin-taleplerim': LeaveListPane,
  'masraf-talep': ExpenseFormPane,
  'masraf-takip': ExpenseTrackingPane,
  mesajlar: MessagesPane,
  bildirimler: AnnouncementsPane,
  'sistem-hata-bildirimleri': BugReportPane,
  'cv-tr': CvPane,
  'cv-en': CvPane,
  gcp: CvPane,
  iata: CvPane,
};

const DEFAULT_BADGES = { dosyalar: 3, 'iletisim-bildirim': 2, mesajlar: 2 };

/**
 * The profile page: the profile card, the section nav and the open tab.
 * `scroll` 0 shows the card; 1 scrolls it away so the nav sticks under the
 * top bar. `paneScroll` scrolls a long tab under the nav.
 * state: { scroll, paneScroll, tab, hero, pane, badges, navHover, frame,
 *          overlay: 'completion'|'photo'|'phone'|'address'|'password'|'sop'|'quiz'|'training'|'visits'|'expenseEdit',
 *          dialog: {...}, appear }
 */
const ProfileScene = ({ state, p }) => {
  const scroll = state.scroll ?? 1;
  const tab = state.tab || 'takvim';
  const group = groupOf(tab);
  const navTop = navTopAt(scroll);
  const heroTop = heroTopAt(scroll);
  const top = contentTop(navTop, group.key) - (state.paneScroll || 0);
  const Pane = PANES[tab];
  const hero = state.hero || {};
  const dialog = state.dialog || {};
  const appear = state.appear ?? 1;
  const overlay = state.overlay;
  return (
    <AppFrame frame={{ page: 'profile', ...state.frame }}>
      <div style={{ clipPath: 'inset(62px 0 0 0)' }} className="tw-absolute tw-inset-0">
        <ProfileHero top={heroTop} state={hero} />
        <div style={{ clipPath: `inset(${navTop + navHeight(group) - 6}px 0 0 0)` }} className="tw-absolute tw-inset-0">
          {Pane && <Pane top={top} state={{ ...(state.pane || {}), tab }} p={p} />}
        </div>
        <ProfileNav top={navTop} group={group.key} tab={tab} badges={state.badges || DEFAULT_BADGES} hover={state.navHover} />
      </div>
      {overlay === 'completion' && <CompletionPopover top={heroTop} state={hero} />}
      {overlay === 'photo' && <PhotoDialog appear={appear} saving={dialog.saving} />}
      {overlay === 'phone' && <PhoneDialog appear={appear} value={dialog.value} focused={dialog.focused} />}
      {overlay === 'address' && <AddressDialog appear={appear} home={dialog.home} work={dialog.work} focus={dialog.focus} primary={dialog.primary} />}
      {overlay === 'password' && <PasswordDialog appear={appear} filled={dialog.filled} />}
      {(overlay === 'sop' || overlay === 'quiz') && <SopViewerDialog appear={overlay === 'sop' ? appear : 1} unlocked={dialog.unlocked} checked={dialog.checked} />}
      {overlay === 'quiz' && <QuizDialog appear={appear} question={dialog.question} answer={dialog.answer} result={dialog.result} />}
      {overlay === 'training' && <TrainingDialog appear={appear} progress={dialog.progress} pop={dialog.pop} popAnswer={dialog.popAnswer} examReady={dialog.examReady} />}
      {overlay === 'visits' && <VisitsDialog appear={appear} state={dialog} />}
      {overlay === 'expenseEdit' && <ExpenseEditDialog appear={appear} />}
    </AppFrame>
  );
};

ProfileScene.propTypes = { state: PropTypes.object.isRequired, p: PropTypes.number };

export default ProfileScene;
